import { Input } from '@ohear/ui'

/**
 * Address block reused in the loan-out and personal-payee sections of the
 * tax profile. Stateless — parent owns all values + change handlers so
 * either copy can wire into different paths on the same form object.
 *
 * Country field is a free 2-letter input rather than a select because the
 * downstream tax form (W-9 vs W-8) is what gates US-vs-foreign behavior;
 * the country here is informational for mailing purposes and we don't want
 * the friction of a 200-row dropdown.
 */
export interface AddressValues {
  line1: string | null
  line2: string | null
  city: string | null
  state: string | null
  postal: string | null
  country: string | null
}

export interface AddressFieldsProps {
  value: AddressValues
  onChange: (next: AddressValues) => void
  /** A11y label prefix so loan-out and payee fields don't collide with shared
   *  for=… labels — pass e.g. "loan_out" or "payee". */
  idPrefix: string
  /** Keys for translated strings — caller supplies the rendered text. */
  labels: {
    line1: string
    line2: string
    city: string
    stateRegion: string
    postal: string
    country: string
  }
  disabled?: boolean
}

export function AddressFields({
  value,
  onChange,
  idPrefix,
  labels,
  disabled = false,
}: AddressFieldsProps) {
  const set = <K extends keyof AddressValues>(k: K, v: AddressValues[K]) =>
    onChange({ ...value, [k]: v })

  return (
    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
      <label className="sm:col-span-6 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.line1}</span>
        <Input
          id={`${idPrefix}-line1`}
          value={value.line1 ?? ''}
          onChange={(e) => set('line1', e.target.value || null)}
          disabled={disabled}
          autoComplete="address-line1"
        />
      </label>
      <label className="sm:col-span-6 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.line2}</span>
        <Input
          id={`${idPrefix}-line2`}
          value={value.line2 ?? ''}
          onChange={(e) => set('line2', e.target.value || null)}
          disabled={disabled}
          autoComplete="address-line2"
        />
      </label>
      <label className="sm:col-span-3 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.city}</span>
        <Input
          id={`${idPrefix}-city`}
          value={value.city ?? ''}
          onChange={(e) => set('city', e.target.value || null)}
          disabled={disabled}
          autoComplete="address-level2"
        />
      </label>
      <label className="sm:col-span-1 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.stateRegion}</span>
        <Input
          id={`${idPrefix}-state`}
          value={value.state ?? ''}
          onChange={(e) => set('state', e.target.value || null)}
          disabled={disabled}
          autoComplete="address-level1"
          maxLength={40}
        />
      </label>
      <label className="sm:col-span-1 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.postal}</span>
        <Input
          id={`${idPrefix}-postal`}
          value={value.postal ?? ''}
          onChange={(e) => set('postal', e.target.value || null)}
          disabled={disabled}
          autoComplete="postal-code"
          maxLength={20}
        />
      </label>
      <label className="sm:col-span-1 flex flex-col gap-1 text-[13px]">
        <span className="font-medium">{labels.country}</span>
        <Input
          id={`${idPrefix}-country`}
          value={value.country ?? ''}
          onChange={(e) => set('country', (e.target.value || '').toUpperCase().slice(0, 2) || null)}
          placeholder="US"
          disabled={disabled}
          autoComplete="country"
          maxLength={2}
        />
      </label>
    </div>
  )
}
